// @flow
import invariant from 'invariant';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { space } from 'styled-system';
import { STATUSES, LISTING_LOCATION_TYPE } from 'utils/constants';
import { APPLICATION_STATUSES } from 'utils/constants/ats';
import type { ListingLocationType } from 'api/shared';
import Box from 'components/Box';
import { colors, mediaRetina } from '../theme';

const ALL_STATUSES = {
  ...STATUSES,
  ...APPLICATION_STATUSES,
  INACTIVE: getText('Inactive'),
  ACTIVE: getText('Active')
};

const getStatus = status => ALL_STATUSES[status];

export const Tag = (props: Object) => (
  <Box
    data-qa-id="listing-status"
    display="inline-block"
    py="2px"
    px="5px"
    borderRadius="4px"
    letterSpacing="1px"
    textTransform="uppercase"
    lineHeight={1}
    fontSize={12}
    {...props}
  />
);

const getLocationStyles = (type: ListingLocationType) => {
  switch (type) {
    case 'REMOTE':
      return {
        bg: colors.fuchsia,
        color: 'white'
      };
    case 'REMOTE_TEMPORARY_COVID':
      return {
        bg: colors.dandelion,
        color: colors.charcoalGrey
      };
    case 'ONSITE':
    default:
      return {
        bg: colors.turtleGreen,
        color: 'white'
      };
  }
};

export const LocationLabel = ({
  type,
  ...props
}: {
  type: ListingLocationType
}) => (
  <Tag {...getLocationStyles(type)} {...props}>
    {LISTING_LOCATION_TYPE[type]}
  </Tag>
);

const StyledStatus = styled.div`
  position: relative;
  font-size: 14px;
  letter-spacing: 1.8px;
  color: ${colors.battleshipGrey};
  text-transform: uppercase;
  line-height: 1.35;
  padding-left: 21px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 4px;
    border-radius: 50%;
    ${props =>
      props.indicatorStyle === 'outline'
        ? css`
            width: 8px;
            height: 8px;
            border: solid 1px ${props.color};
            @media ${mediaRetina} {
              border-width: 1.5px;
            }
          `
        : css`
            width: 11px;
            height: 11px;
            background-color: ${props.color};
          `};
  }
  ${space};
`;

const ListingStatusLabel = ({
  listingStatus,
  statusNote,
  asTag,
  ...props
}: {
  listingStatus: $ReadOnlyArray<string> | string,
  statusNote?: ?string,
  asTag?: boolean
}) => {
  let status;
  let color = colors.scarlet;
  let textColor = 'white';
  let indicatorStyle = 'outline';

  if (listingStatus.includes('PUBLISHED')) {
    status = 'PUBLISHED';
    color = colors.turtleGreen;
    indicatorStyle = 'solid';
  } else if (listingStatus.includes('REJECTED')) {
    status = 'REJECTED';
    color = colors.dandelion;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('DRAFT')) {
    status = 'DRAFT';
    color = colors.dandelion;
    textColor = 'white';
  } else if (listingStatus.includes('PENDING')) {
    status = 'PENDING';
    color = colors.dandelion;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('INVOICE')) {
    status = 'INVOICE';
    color = colors.turtleGreen;
  } else if (listingStatus.includes('ARCHIVED')) {
    status = 'ARCHIVED';
    color = colors.dandelion;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('FROZEN')) {
    status = 'FROZEN';
  } else if (listingStatus.includes('HIDDEN')) {
    status = 'HIDDEN';
    color = colors.dandelion;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('PULLED')) {
    status = 'PULLED';
  } else if (listingStatus.includes('DELETED')) {
    status = 'DELETED';
  } else if (listingStatus.includes('CHECKOUT')) {
    status = 'CHECKOUT';
    color = colors.dandelion;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('SPAM')) {
    status = 'SPAM';
  } else if (listingStatus.includes('APPROVED')) {
    status = 'APPROVED';
    color = colors.turtleGreen;
    indicatorStyle = 'solid';
  } else if (listingStatus.includes('NEW')) {
    color = colors.waterBlue;
  } else if (listingStatus.includes('YES')) {
    color = colors.turtleGreen;
  } else if (listingStatus.includes('NO')) {
    color = colors.silver;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('MAYBE')) {
    color = colors.dandelion;
    textColor = colors.charcoalGrey;
  } else if (listingStatus === 'ACTIVE') {
    color = colors.turtleGreen;
  } else if (listingStatus === 'INACTIVE') {
    color = colors.silver;
    textColor = colors.charcoalGrey;
  }

  if (
    typeof listingStatus === 'string' &&
    Object.keys(ALL_STATUSES).includes(listingStatus)
  ) {
    status = listingStatus;
    indicatorStyle = 'solid';
  }

  invariant(status, 'Invalid status');

  return asTag ? (
    <Tag bg={color} color={textColor} {...props}>
      {getStatus(status)}
      {statusNote && ` (${statusNote})`}
    </Tag>
  ) : (
    <StyledStatus
      data-qa-id="listing-status"
      color={color}
      indicatorStyle={indicatorStyle}
      {...props}
    >
      {getStatus(status)}
      {statusNote && ` (${statusNote})`}
    </StyledStatus>
  );
};

export default ListingStatusLabel;
